import { PERMISSIONS, type PermissionCode } from '@asyagrup/group-hub-contracts';
import {
  Banknote,
  BedDouble,
  Bug,
  CalendarPlus,
  Contact,
  Lightbulb,
  Receipt,
  Tags,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { KATEGORILER_ADRESI } from '@/features/announcements/duyuru-sekmesi';
import { fold } from './screens';

/**
 * "İşlemler" — the second client-side tier.
 *
 * A launcher that only jumps to screens makes people navigate and then hunt
 * for the button. These rows are the eight things somebody opens this box to
 * DO, addressed by the three query-string deep links that already exist plus
 * the list screens that own the rest.
 *
 * `requires` is `PermissionCode[]`, typed against the same enum the backend
 * guard reads, so a mistyped code is TS2322 here at the definition site rather
 * than a row that quietly never appears for anyone.
 *
 * Note what each entry asks for: the permission to PERFORM the action AND the
 * permission to reach the screen that hosts it. Filing a payee is
 * `payee.manage`, but `/cari` opens on `payment_request.create` — an action row
 * gated on the write alone would offer a shortcut that lands on a screen the
 * router refuses. Two codes, one row, no dead end.
 */
export interface SearchAction {
  key: string;
  label: string;
  /**
   * What people actually type. "izin talebi oluştur" is not it — "izin",
   * "tatil" and "rapor" are, and none of them is a substring of the label.
   */
  keywords: string[];
  href: string;
  requires: PermissionCode[];
  icon: LucideIcon;
}

export const SEARCH_ACTIONS: SearchAction[] = [
  {
    key: 'leave-create',
    label: 'İzin talebi oluştur',
    keywords: ['izin', 'tatil', 'rapor', 'yıllık', 'mazeret'],
    href: '/izinlerim',
    requires: [PERMISSIONS.LEAVE_REQUEST_CREATE],
    icon: CalendarPlus,
  },
  {
    // `?yeni=hata` and `?yeni=gelistirme` are read by the support screen's own
    // url-state; search uses the parameters that exist rather than inventing a
    // scheme, and adds none of its own.
    key: 'support-bug',
    label: 'Hata bildir',
    keywords: ['hata', 'bug', 'arıza', 'çalışmıyor', 'sorun', 'destek'],
    href: '/destek?yeni=hata',
    requires: [PERMISSIONS.SUPPORT_CREATE, PERMISSIONS.SUPPORT_READ_OWN],
    icon: Bug,
  },
  {
    key: 'support-feature',
    label: 'Geliştirme öner',
    keywords: ['geliştirme', 'öneri', 'istek', 'talep', 'destek'],
    href: '/destek?yeni=gelistirme',
    requires: [PERMISSIONS.SUPPORT_CREATE, PERMISSIONS.SUPPORT_READ_OWN],
    icon: Lightbulb,
  },
  {
    key: 'payment-create',
    label: 'Ödeme talebi oluştur',
    keywords: ['ödeme', 'fatura', 'havale', 'eft', 'tediye'],
    href: '/odeme-tablosu',
    requires: [PERMISSIONS.PAYMENT_REQUEST_CREATE],
    icon: Banknote,
  },
  {
    key: 'expense-create',
    label: 'Masraf talebi oluştur',
    keywords: ['masraf', 'harcama', 'fiş', 'yol', 'yemek'],
    href: '/taleplerim',
    requires: [PERMISSIONS.EXPENSE_CREATE],
    icon: Receipt,
  },
  {
    key: 'payee-create',
    label: 'Cari kayıt ekle',
    keywords: ['cari', 'tedarikçi', 'firma', 'iban', 'hesap'],
    href: '/cari',
    requires: [PERMISSIONS.PAYEE_MANAGE, PERMISSIONS.PAYMENT_REQUEST_CREATE],
    icon: Contact,
  },
  {
    key: 'reservation-create',
    label: 'Rezervasyon oluştur',
    keywords: ['rezervasyon', 'misafir', 'konaklama', 'oda', 'yatak'],
    href: '/misafirhane/rezervasyon',
    requires: [PERMISSIONS.KONAKLAMA_RESERVATION_MANAGE, PERMISSIONS.KONAKLAMA_READ],
    icon: BedDouble,
  },
  {
    // Kategori sözlüğü artık Duyurular'ın bir sekmesi ve menüde kendi satırı
    // yok — «Ekranlar» grubu menü ETİKETİNDEN eşleşiyor, yani «kategori» yazan
    // «Duyurular» satırını bulamaz (`NavItem.sections` yalnız eşleşmiş bir
    // satırın İÇİNDE yarı seçiyor). Buradaki satır sözlüğü bulunur tutuyor.
    // İki kod, aynı gerekçeyle: sekme `category_manage` ister, onu taşıyan
    // ekran `announcement.read`.
    key: 'announcement-categories',
    label: 'Duyuru kategorilerini yönet',
    keywords: ['kategori', 'duyuru', 'memorandum', 'sözlük'],
    href: KATEGORILER_ADRESI,
    requires: [PERMISSIONS.ANNOUNCEMENT_CATEGORY_MANAGE, PERMISSIONS.ANNOUNCEMENT_READ],
    icon: Tags,
  },
];

export function matchActions(
  term: string,
  can: (permission: PermissionCode) => boolean,
  limit = 5,
): SearchAction[] {
  const needle = fold(term);
  if (needle.length < 2) return [];

  return SEARCH_ACTIONS.filter((action) => action.requires.every((p) => can(p)))
    .filter(
      (action) =>
        fold(action.label).includes(needle) ||
        action.keywords.some((keyword) => fold(keyword).startsWith(needle)),
    )
    .slice(0, limit);
}
